import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import TinyMCEEmailEditor from "../../components/LexicalEmailEditor";
import TemplateBrowser from "../../components/marketing/TemplateBrowser";
import "./MarketingCampaigns.css";

const MarketingCampaigns = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [view, setView] = useState("list"); // list, create, template-picker
  const [showTemplateBrowser, setShowTemplateBrowser] = useState(false);
  const [businessId, setBusinessId] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [shops, setShops] = useState([]);
  const [staffProfile, setStaffProfile] = useState(null);
  const [showLaptopBanner, setShowLaptopBanner] = useState(true);

  // Replace merge fields with actual business data for preview
  const replaceMergeFields = useCallback(
    (content) => {
      if (!content) return content;

      // Smart URL generation based on business type
      let businessUrl = "#";
      let bookingUrl = "#";

      if (businessData && shops.length > 0) {
        const firstShop = shops[0];

        // Check if this is a freelancer (virtual location)
        if (firstShop.isVirtualLocation && firstShop.slug) {
          // Freelancer: Use /freelancer/:slug
          businessUrl = `${window.location.origin}/freelancer/${firstShop.slug}`;
          bookingUrl = `${window.location.origin}/freelancer/${firstShop.slug}/services`;
        } else if (shops.length > 1) {
          // Multi-location business: Use /business/:businessSlug (overview page)
          businessUrl = `${window.location.origin}/business/${businessData.slug}`;
          bookingUrl = `${window.location.origin}/business/${businessData.slug}`;
        } else if (shops.length === 1 && businessData.slug && firstShop.slug) {
          // Single-location business: Use /business/:businessSlug/:locationSlug
          businessUrl = `${window.location.origin}/business/${businessData.slug}/${firstShop.slug}`;
          bookingUrl = `${window.location.origin}/business/${businessData.slug}/${firstShop.slug}`;
        }
      }

      // Use actual business data if available, otherwise use sample data
      const sampleData = {
        firstName: "Sarah",
        lastName: "Johnson",
        email: "sarah@example.com",
        businessName: businessData?.name || "Your Business",
        businessEmail:
          businessData?.businessEmail ||
          businessData?.email ||
          "contact@yourbusiness.com",
        businessPhone:
          businessData?.businessPhone ||
          businessData?.phone ||
          "(555) 123-4567",
        businessAddress: businessData
          ? `${businessData.street || ""}, ${businessData.city || ""}, ${businessData.state || ""} ${businessData.zipCode || ""}`
              .trim()
              .replace(/^,\s*/, "")
              .replace(/,\s*$/, "")
          : "123 Main Street, City, ST 12345",
        businessLogo:
          staffProfile?.brandImage ||
          businessData?.logo?.url ||
          shops[0]?.logo?.url ||
          shops[0]?.images?.[0]?.url ||
          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='90'%3E%3Crect fill='%23f0f0f0' width='200' height='90'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-family='Arial' font-size='14'%3ENo Logo%3C/text%3E%3C/svg%3E",
        shopName: shops[0]?.name || "Your Shop",
        productName: "Premium Facial Package",
        productDescription: "Rejuvenating 60-minute facial treatment",
        productPrice: "99",
        discountCode: "SAVE20",
        discountAmount: "20%",
        businessUrl,
        bookingUrl,
        websiteUrl: businessData?.website || "#",
        unsubscribeUrl: "#",
        month: new Date().toLocaleString("en-US", { month: "long" }),
        year: new Date().getFullYear().toString(),
      };

      let preview = content;
      Object.keys(sampleData).forEach((key) => {
        const regex = new RegExp(`\\{\\{${key}\\}\\}`, "g");
        preview = preview.replace(regex, sampleData[key]);
      });

      return preview;
    },
    [businessData, shops, staffProfile],
  );

  // Campaign form state
  const [campaignForm, setCampaignForm] = useState({
    name: "",
    subject: "",
    content: "",
    audienceType: "all_customers",
    scheduledFor: "",
  });

  // Memoize preview content to prevent re-renders on every keystroke
  const previewContent = useMemo(() => {
    return replaceMergeFields(campaignForm.content);
  }, [campaignForm.content, replaceMergeFields]); // Only recalculate when these change

  const previewSubject = useMemo(() => {
    return replaceMergeFields(campaignForm.subject);
  }, [campaignForm.subject, replaceMergeFields]);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const getApiBaseUrl = useCallback(() => {
    return process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
  }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const res = await fetch(`${getApiBaseUrl()}/api/marketing/campaigns`, {
        headers,
      });

      if (res.ok) {
        const data = await res.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (err) {
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  const fetchTemplates = useCallback(
    async (businessIdParam = null) => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        // Build URL with businessId parameter if available
        const params = new URLSearchParams();
        const idToUse = businessIdParam || businessId;
        if (idToUse) {
          params.append("businessId", idToUse);
        }

        const url = `${getApiBaseUrl()}/api/marketing/templates?${params.toString()}`;

        const res = await fetch(url, {
          headers,
        });

        if (res.ok) {
          const data = await res.json();
          setTemplates(data.templates || []);
        }
      } catch (err) {
      }
    },
    [getAuthHeaders, getApiBaseUrl, businessId],
  );

  const fetchUserProfile = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      // Decode role from JWT to avoid unnecessary 404s for non-freelancer roles
      let userRole = null;
      try {
        const token = localStorage.getItem("ogestyle_token");
        if (token) {
          const payload = JSON.parse(atob(token.split(".")[1]));
          userRole = payload.role;
        }
      } catch (_) {
        // If decode fails, proceed without role check
      }
      const isFreelancerRole = userRole === "R4" || userRole === "R7";

      let businessIdToUse = null;
      const parallelRequests = [
        fetch(`${getApiBaseUrl()}/api/businesses/my-businesses`, { headers }),
        fetch(`${getApiBaseUrl()}/api/shops/my-shops`, { headers }),
      ];

      if (isFreelancerRole) {
        parallelRequests.splice(
          1,
          0,
          fetch(`${getApiBaseUrl()}/api/freelancer/profile`, { headers }),
        );
      }

      const results = await Promise.all(parallelRequests);
      const businessesRes = results[0];
      const freelancerProfileRes = isFreelancerRole ? results[1] : null;
      const shopsRes = isFreelancerRole ? results[2] : results[1];

      if (businessesRes.ok) {
        const businessesData = await businessesRes.json();
        if (businessesData.businesses && businessesData.businesses.length > 0) {
          businessIdToUse = businessesData.businesses[0]._id;
        }
      }

      if (freelancerProfileRes && freelancerProfileRes.ok) {
        const freelancerProfileData = await freelancerProfileRes.json();
        setStaffProfile(freelancerProfileData.staff || null);

        if (!businessIdToUse) {
          businessIdToUse = freelancerProfileData.staff?.businessId || null;
        }
      } else {
        setStaffProfile(null);
      }

      if (shopsRes.ok) {
        const shopsData = await shopsRes.json();
        setShops(shopsData.shops || []);
      } else {
        setShops([]);
      }

      setBusinessId(businessIdToUse || null);

      // Fetch business details if we have a businessId
      if (businessIdToUse) {
        const businessRes = await fetch(
          `${getApiBaseUrl()}/api/businesses/${businessIdToUse}`,
          {
            headers,
          },
        );

        if (businessRes.ok) {
          setBusinessData(await businessRes.json());
        } else {
          setBusinessData(null);
        }
      } else {
        setBusinessData(null);
      }
    } catch (err) {
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchCampaigns(),
        fetchTemplates(), // Initial fetch without businessId (global templates only)
        fetchUserProfile(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchCampaigns, fetchTemplates, fetchUserProfile]);

  // Refetch templates when businessId becomes available
  useEffect(() => {
    if (businessId) {
      fetchTemplates(businessId);
    }
  }, [businessId, fetchTemplates]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCampaignForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const headers = {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      };
      if (!headers.Authorization) return;

      // Check if we're editing an existing campaign
      const isEditing = campaignForm._id;
      const url = isEditing
        ? `${getApiBaseUrl()}/api/marketing/campaigns/${campaignForm._id}`
        : `${getApiBaseUrl()}/api/marketing/campaigns`;
      const method = isEditing ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify({
          ...campaignForm,
          type: "email",
          _id: undefined, // Remove _id from body
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (isEditing) {
          // Update existing campaign in list
          setCampaigns((prev) =>
            prev.map((c) => (c._id === data.campaign._id ? data.campaign : c)),
          );
          setSuccess("Campaign updated successfully!");
        } else {
          // Add new campaign to list
          setCampaigns((prev) => [data.campaign, ...prev]);
          setSuccess("Campaign created successfully!");
        }
        setTimeout(() => setSuccess(null), 3000);
        setView("list");
        setCampaignForm({
          name: "",
          subject: "",
          content: "",
          audienceType: "all_customers",
          scheduledFor: "",
        });
      } else {
        const errorData = await res.json();
        setError(errorData.error || "Failed to create campaign");
      }
    } catch (err) {
      setError("Failed to create campaign");
    }
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm("Send this campaign now? This cannot be undone.")) {
      return;
    }

    try {
      const headers = {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      };
      if (!headers.Authorization) return;

      const res = await fetch(
        `${getApiBaseUrl()}/api/marketing/campaigns/${campaignId}/send`,
        {
          method: "POST",
          headers,
        },
      );

      if (res.ok) {
        const data = await res.json();
        const stats = data.stats || {};
        setSuccess(
          `Campaign sent successfully! Delivered to ${stats.sent || 0} recipient(s)${stats.failed > 0 ? `, ${stats.failed} failed` : ""}`,
        );
        setTimeout(() => setSuccess(null), 5000);
        fetchCampaigns();
      } else {
        const errorData = await res.json();
        setError(
          errorData.message || errorData.error || "Failed to send campaign",
        );
      }
    } catch (err) {
      setError("Failed to send campaign");
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this draft campaign? This cannot be undone.",
      )
    ) {
      return;
    }

    try {
      const headers = {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      };
      if (!headers.Authorization) return;

      const res = await fetch(
        `${getApiBaseUrl()}/api/marketing/campaigns/${campaignId}`,
        {
          method: "DELETE",
          headers,
        },
      );

      if (res.ok) {
        setSuccess("Campaign deleted successfully!");
        setTimeout(() => setSuccess(null), 3000);
        fetchCampaigns();
      } else {
        const errorData = await res.json();
        setError(errorData.error || "Failed to delete campaign");
      }
    } catch (err) {
      setError("Failed to delete campaign");
    }
  };

  const handleEditCampaign = (campaign) => {
    // Force view to list first to ensure clean unmount
    setView("list");

    // Then update state and switch to create view
    setTimeout(() => {
      // Format scheduledFor for datetime-local input (YYYY-MM-DDTHH:MM)
      let formattedScheduledFor = "";
      if (campaign.scheduledFor) {
        const date = new Date(campaign.scheduledFor);
        // Adjust for local timezone
        date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
        formattedScheduledFor = date.toISOString().slice(0, 16);
      }

      // Update form with campaign data
      setCampaignForm({
        name: campaign.name,
        subject: campaign.subject,
        content: campaign.content || "",
        audienceType: campaign.audienceType || "all_customers",
        scheduledFor: formattedScheduledFor,
        _id: campaign._id, // Store campaign ID for updating
      });

      // Switch to create view after state is fully updated
      setTimeout(() => {
        setView("create");
      }, 100);
    }, 10);
  };

  const handleUseTemplate = (template) => {
    // Replace merge fields with empty strings before setting content
    let contentWithPlaceholders = template.content || "";

    // Replace common image merge fields with empty strings (user must provide actual images)
    const imagePlaceholders = {
      "{{businessLogo}}": "",
      "{{productImageUrl}}": "",
      "{{heroImageUrl}}": "",
    };

    Object.keys(imagePlaceholders).forEach((field) => {
      const regex = new RegExp(field.replace(/[{}]/g, "\\$&"), "g");
      contentWithPlaceholders = contentWithPlaceholders.replace(
        regex,
        imagePlaceholders[field],
      );
    });

    setCampaignForm({
      name: `${template.name} - ${new Date().toLocaleDateString()}`,
      subject: template.subject || "",
      content: contentWithPlaceholders,
      audienceType: "all_customers",
      scheduledFor: "",
    });

    // Small delay to ensure state is updated before switching view
    setTimeout(() => {
      setView("create");
    }, 50);
  };

  const handleTemplateSelect = (template) => {
    if (template) {
      // Keep merge fields intact - the preview will replace them with actual data
      const contentWithMergeFields = template.content || "";

      // Use selected template with merge fields preserved
      setCampaignForm({
        name: `${template.templateName} - ${new Date().toLocaleDateString()}`,
        subject: template.subject || "",
        content: contentWithMergeFields,
        audienceType: template.audienceType || "all_customers",
        scheduledFor: "",
      });
    } else {
      // Start from scratch (blank template)
      setCampaignForm({
        name: "",
        subject: "",
        content: "",
        audienceType: "all_customers",
        scheduledFor: "",
      });
    }
    setView("create");
  };

  const handleOpenTemplateBrowser = () => {
    setShowTemplateBrowser(true);
  };

  const renderCampaignList = () => {
    // Determine SMS credits path based on current route
    const path = window.location.pathname;
    const smsCreditsPath = path.includes("/booth-renter/")
      ? "/dashboard/booth-renter/marketing/sms-credits"
      : path.includes("/freelancer/")
        ? "/dashboard/freelancer/marketing/sms-credits"
        : "/dashboard/marketing/sms-credits";

    return (
      <div className="marketing-container">
        {/* Header */}
        <div className="marketing-header">
          <div className="header-content">
            <h1>Email Campaigns</h1>
            <p>Create and send beautiful email campaigns to your customers</p>
          </div>
          <div className="header-actions">
            <button
              className="btn btn-secondary"
              onClick={() => navigate(smsCreditsPath)}
              style={{ marginRight: "8px" }}
            >
              💳 SMS Credits
            </button>
            <button
              className="btn btn-outline"
              onClick={handleOpenTemplateBrowser}
            >
              📋 Browse Templates
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                setCampaignForm({
                  name: "",
                  subject: "",
                  content: "",
                  audienceType: "all_customers",
                  scheduledFor: "",
                });
                setView("create");
              }}
            >
              <span className="btn-icon">+</span>
              New Campaign
            </button>
          </div>
        </div>

        {/* Laptop Recommendation Banner */}
        {showLaptopBanner && (
          <div className="laptop-banner">
            <div className="laptop-banner-content">
              <span className="laptop-banner-icon">💻</span>
              <div className="laptop-banner-text">
                <strong>Best Experience on Laptop/Desktop</strong>
                <p>
                  For the full campaign management experience with detailed
                  analytics and customer lists, we recommend using a laptop or
                  desktop computer.
                </p>
              </div>
            </div>
            <button
              className="laptop-banner-close"
              onClick={() => setShowLaptopBanner(false)}
              aria-label="Close banner"
            >
              ×
            </button>
          </div>
        )}

        {/* Stats Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <span style={{ fontSize: "20px", marginRight: "8px" }}>📊</span>
              <span
                style={{
                  fontSize: "13px",
                  color: "#6b7280",
                  fontWeight: 600,
                }}
              >
                Total Campaigns
              </span>
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: 700, color: "#6366f1" }}
            >
              {campaigns.length}
            </div>
          </div>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <span style={{ fontSize: "20px", marginRight: "8px" }}>✅</span>
              <span
                style={{
                  fontSize: "13px",
                  color: "#6b7280",
                  fontWeight: 600,
                }}
              >
                Sent
              </span>
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: 700, color: "#10b981" }}
            >
              {campaigns.filter((c) => c.status === "sent").length}
            </div>
          </div>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <span style={{ fontSize: "20px", marginRight: "8px" }}>📝</span>
              <span
                style={{
                  fontSize: "13px",
                  color: "#6b7280",
                  fontWeight: 600,
                }}
              >
                Drafts
              </span>
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: 700, color: "#9ca3af" }}
            >
              {campaigns.filter((c) => c.status === "draft").length}
            </div>
          </div>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <span style={{ fontSize: "20px", marginRight: "8px" }}>⏰</span>
              <span
                style={{
                  fontSize: "13px",
                  color: "#6b7280",
                  fontWeight: 600,
                }}
              >
                Scheduled
              </span>
            </div>
            <div
              style={{ fontSize: "32px", fontWeight: 700, color: "#f59e0b" }}
            >
              {campaigns.filter((c) => c.status === "scheduled").length}
            </div>
          </div>
        </div>

        {/* Campaigns Grid */}
        <div className="campaigns-section">
          <h2>Your Campaigns</h2>
          {campaigns.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📧</div>
              <h3>No campaigns yet</h3>
              <p>
                Create your first email campaign to start engaging customers
              </p>
              <button
                className="btn btn-primary"
                onClick={() => setView("create")}
              >
                Create Campaign
              </button>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                gap: "20px",
              }}
            >
              {campaigns.map((campaign) => {
                const getStatusColor = (status) => {
                  switch (status) {
                    case "sent":
                      return {
                        bg: "#d1fae5",
                        color: "#065f46",
                        border: "#10b981",
                      };
                    case "scheduled":
                      return {
                        bg: "#fef3c7",
                        color: "#92400e",
                        border: "#f59e0b",
                      };
                    case "draft":
                      return {
                        bg: "#e5e7eb",
                        color: "#374151",
                        border: "#9ca3af",
                      };
                    case "sending":
                      return {
                        bg: "#dbeafe",
                        color: "#1e40af",
                        border: "#3b82f6",
                      };
                    default:
                      return {
                        bg: "#f3f4f6",
                        color: "#6b7280",
                        border: "#d1d5db",
                      };
                  }
                };

                const statusStyle = getStatusColor(campaign.status);

                return (
                  <div
                    key={campaign._id}
                    style={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "12px",
                      padding: "20px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "12px",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: "16px",
                          fontWeight: 700,
                          color: "#111827",
                          margin: 0,
                          flex: 1,
                        }}
                      >
                        {campaign.name}
                      </h3>
                      <span
                        style={{
                          padding: "4px 12px",
                          backgroundColor: statusStyle.bg,
                          color: statusStyle.color,
                          border: `1px solid ${statusStyle.border}`,
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      >
                        {campaign.status}
                      </span>
                    </div>

                    <div
                      style={{
                        fontSize: "14px",
                        color: "#6b7280",
                        lineHeight: "1.6",
                        marginBottom: "12px",
                      }}
                    >
                      {campaign.subject && (
                        <div style={{ marginBottom: "8px" }}>
                          <strong style={{ color: "#374151" }}>Subject:</strong>{" "}
                          {campaign.subject}
                        </div>
                      )}
                      {campaign.audienceType && (
                        <div style={{ marginBottom: "8px" }}>
                          <strong style={{ color: "#374151" }}>
                            Audience:
                          </strong>{" "}
                          {campaign.audienceType.replace(/_/g, " ")}
                        </div>
                      )}
                      {campaign.scheduledFor && (
                        <div style={{ marginBottom: "8px" }}>
                          <strong style={{ color: "#374151" }}>
                            Scheduled:
                          </strong>{" "}
                          {new Date(campaign.scheduledFor).toLocaleString()}
                        </div>
                      )}
                      {campaign.sentAt && (
                        <div style={{ marginBottom: "8px" }}>
                          <strong style={{ color: "#374151" }}>Sent:</strong>{" "}
                          {new Date(campaign.sentAt).toLocaleString()}
                        </div>
                      )}
                    </div>

                    {campaign.analytics && (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(80px, 1fr))",
                          gap: "12px",
                          padding: "12px",
                          backgroundColor: "#f9fafb",
                          borderRadius: "8px",
                          marginBottom: "12px",
                        }}
                      >
                        <div style={{ textAlign: "center" }}>
                          <div
                            style={{
                              fontSize: "20px",
                              fontWeight: 700,
                              color: "#111827",
                            }}
                          >
                            {campaign.analytics.totalSent || 0}
                          </div>
                          <div style={{ fontSize: "12px", color: "#6b7280" }}>
                            Sent
                          </div>
                        </div>
                        {campaign.type === "email" && (
                          <>
                            <div style={{ textAlign: "center" }}>
                              <div
                                style={{
                                  fontSize: "20px",
                                  fontWeight: 700,
                                  color: "#10b981",
                                }}
                              >
                                {campaign.analytics.opens || 0}
                              </div>
                              <div
                                style={{ fontSize: "12px", color: "#6b7280" }}
                              >
                                Opens
                              </div>
                            </div>
                            <div style={{ textAlign: "center" }}>
                              <div
                                style={{
                                  fontSize: "20px",
                                  fontWeight: 700,
                                  color: "#6366f1",
                                }}
                              >
                                {campaign.analytics.clicks || 0}
                              </div>
                              <div
                                style={{ fontSize: "12px", color: "#6b7280" }}
                              >
                                Clicks
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        marginTop: "12px",
                        flexWrap: "wrap",
                      }}
                    >
                      {campaign.status === "draft" && (
                        <>
                          <button
                            className="btn btn-small btn-secondary"
                            onClick={() => handleEditCampaign(campaign)}
                            style={{ flex: 1 }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-small"
                            onClick={() => handleSendCampaign(campaign._id)}
                            style={{ flex: 1 }}
                          >
                            Send
                          </button>
                          <button
                            className="btn btn-small btn-danger"
                            onClick={() => handleDeleteCampaign(campaign._id)}
                            style={{
                              backgroundColor: "#dc3545",
                              color: "white",
                            }}
                          >
                            Delete
                          </button>
                        </>
                      )}
                      {(campaign.status === "sent" ||
                        campaign.status === "scheduled") && (
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() =>
                            navigate(
                              `/dashboard/marketing/campaign/${campaign._id}`,
                            )
                          }
                          style={{ flex: 1 }}
                        >
                          View Details
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCreateForm = () => (
    <div className="marketing-container">
      <div className="marketing-header">
        <div className="header-content">
          <button className="btn-back" onClick={() => setView("list")}>
            ← Back to Campaigns
          </button>
          <h1>Create Email Campaign</h1>
        </div>
      </div>

      <form onSubmit={handleCreateCampaign} className="campaign-form">
        {/* Top Section - Campaign Details */}
        <div className="form-section campaign-details-section">
          <h3>Campaign Details</h3>
          <div className="details-grid">
            <div className="form-field">
              <label htmlFor="name">Campaign Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={campaignForm.name}
                onChange={handleInputChange}
                placeholder="e.g., Summer Sale 2026"
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="subject">Email Subject *</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={campaignForm.subject}
                onChange={handleInputChange}
                placeholder="Your email subject line"
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="audienceType">Target Audience *</label>
              <select
                id="audienceType"
                name="audienceType"
                value={campaignForm.audienceType}
                onChange={handleInputChange}
                required
              >
                <option value="all_customers">All Customers</option>
                <option value="guest_leads">Newsletter Subscribers</option>
                <option value="active_customers">
                  Active Customers (90 days)
                </option>
                <option value="inactive_customers">Inactive Customers</option>
                <option value="high_value_customers">
                  High Value Customers
                </option>
                <option value="cart_abandoned">Cart Abandoned</option>
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="scheduledFor">Schedule Send (Optional)</label>
              <input
                type="datetime-local"
                id="scheduledFor"
                name="scheduledFor"
                value={campaignForm.scheduledFor}
                onChange={handleInputChange}
                min={(() => {
                  const now = new Date();
                  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
                  return now.toISOString().slice(0, 16);
                })()}
              />
              <small>Leave blank to save as draft</small>
            </div>
          </div>
        </div>

        {/* Email Content Section - Full Width */}
        <div className="form-section email-content-section">
          <h3>Email Content</h3>
          <div
            style={{
              background: "#e7f3ff",
              border: "1px solid #90caf9",
              borderRadius: "6px",
              padding: "12px 16px",
              marginBottom: "16px",
              fontSize: "14px",
              color: "#1565c0",
              display: "flex",
              gap: "10px",
              alignItems: "flex-start",
            }}
          >
            <span style={{ fontSize: "18px", flexShrink: 0 }}>ℹ️</span>
            <div>
              <strong>Footer automatically added:</strong> A professional footer
              with your business details and unsubscribe link will be
              automatically added to every email. Do not add footer content in
              your email body.
            </div>
          </div>
          <TinyMCEEmailEditor
            value={campaignForm.content}
            onChange={(html) =>
              setCampaignForm((prev) => ({ ...prev, content: html }))
            }
            placeholder="Write your email content here. Use the toolbar to format text, add headings, links, and more..."
            businessId={businessId}
          />
        </div>

        {/* Preview Section - Side by Side Desktop and Mobile */}
        <div className="form-section preview-section-wrapper">
          <h3>Email Preview</h3>
          <div className="preview-grid">
            {/* Desktop Preview */}
            <div className="preview-container desktop-preview">
              <div className="preview-label">
                <span>🖥️ Desktop View</span>
              </div>
              <div className="email-preview">
                <div className="email-header">
                  <div className="email-subject">
                    {previewSubject || "Subject line"}
                  </div>
                  <div className="email-from">
                    From: {businessData?.businessName || "Your Business"}
                  </div>
                </div>
                <div
                  className="email-body"
                  dangerouslySetInnerHTML={{
                    __html:
                      previewContent ||
                      "<p>Your email content will appear here...</p>",
                  }}
                />
              </div>
            </div>

            {/* Mobile Preview */}
            <div className="preview-container mobile-preview">
              <div className="preview-label">
                <span>📱 Mobile View</span>
              </div>
              <div className="email-preview mobile">
                <div className="email-header">
                  <div className="email-subject">
                    {previewSubject || "Subject line"}
                  </div>
                  <div className="email-from">
                    From: {businessData?.businessName || "Your Business"}
                  </div>
                </div>
                <div
                  className="email-body"
                  dangerouslySetInnerHTML={{
                    __html:
                      previewContent ||
                      "<p>Your email content will appear here...</p>",
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => setView("list")}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            {campaignForm.scheduledFor ? "Schedule Campaign" : "Save Draft"}
          </button>
        </div>
      </form>
    </div>
  );

  const renderTemplatePicker = () => (
    <div className="marketing-container">
      <div className="marketing-header">
        <div className="header-content">
          <button className="btn-back" onClick={() => setView("list")}>
            ← Back to Campaigns
          </button>
          <h1>Choose a Template</h1>
        </div>
      </div>

      {templates.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No templates available</h3>
          <p>Templates created by your admin will appear here</p>
        </div>
      ) : (
        <div className="templates-grid">
          {templates.map((template) => (
            <div
              key={template._id}
              className="template-card"
              onClick={() => handleUseTemplate(template)}
            >
              <div className="template-header">
                <h4>{template.name}</h4>
              </div>
              {template.subject && (
                <div className="template-subject">{template.subject}</div>
              )}
              <div
                className="template-preview"
                dangerouslySetInnerHTML={{
                  __html: template.content.substring(0, 200) + "...",
                }}
              />
              <button className="btn btn-outline btn-block">
                Use Template
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="marketing-loading">
        <div className="spinner"></div>
        <p>Loading campaigns...</p>
      </div>
    );
  }

  return (
    <div className="marketing-page">
      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      {view === "list" && renderCampaignList()}
      {view === "create" && renderCreateForm()}
      {view === "template-picker" && renderTemplatePicker()}

      {/* Template Browser Modal */}
      <TemplateBrowser
        isOpen={showTemplateBrowser}
        onClose={() => setShowTemplateBrowser(false)}
        onSelect={handleTemplateSelect}
        businessId={businessId}
      />
    </div>
  );
};

export default MarketingCampaigns;
